import { differenceInDays } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { safeMoneyFromCents } from '@libs/services/money';
import { Order, Cart, Material } from 'src/trpc';

// Extract ContainerPricing type from Order's quotedPricingDetails
type QuotedPricingDetails = NonNullable<Order['quotedPricingDetails']>;
type Quote = NonNullable<QuotedPricingDetails['quote']>;
type ContainerPricing = Quote['pricing']['containers'][number];

// Extract CartMaterial type from Cart
type CartMaterial = NonNullable<Cart['materials']>[number];

interface FixedPricingCardProps {
  containerPricing: ContainerPricing;
  dropOffDate?: string;
  pickupDate?: string;
  selectedMaterials?: CartMaterial[];
  allMaterials?: Material[];
}

export const FixedPricingCard = ({
  containerPricing,
  dropOffDate,
  pickupDate,
  selectedMaterials,
  allMaterials,
}: FixedPricingCardProps) => {
  const {
    name,
    quantity = 1,
    basePriceInCents,
    includedDays,
    dailyOverageFeeInCents,
    periodTier,
  } = containerPricing;

  // Get selected material name
  const getMaterialName = () => {
    if (!selectedMaterials || selectedMaterials.length === 0 || !allMaterials) {
      return 'single material';
    }
    const material = allMaterials.find((m) => m._id === selectedMaterials[0].id);
    return material ? material.name : 'single material';
  };

  // Calculate actual days and overages
  const calculateDaysInfo = () => {
    if (!pickupDate || !dropOffDate) {
      return null;
    }

    const actualDays = differenceInDays(new Date(pickupDate), new Date(dropOffDate));
    const effectiveIncludedDays = includedDays || periodTier?.selectedPeriodDays || 0;
    const overageDays = Math.max(0, actualDays - effectiveIncludedDays);
    const overageRate = dailyOverageFeeInCents || 0;

    return {
      actualDays,
      includedDays: effectiveIncludedDays,
      overageDays,
      overageRate,
    };
  };

  const daysInfo = calculateDaysInfo();
  const materialName = getMaterialName();
  const effectiveIncludedDays = includedDays || periodTier?.selectedPeriodDays;

  return (
    <div className="card justify-start w-full p-4 mt-2">
      <div className="flex flex-col gap-2 w-full self-start">
        <h3 className="font-semibold text-lg text-primary-900">Single Material Package</h3>

        {/* Single Material Details */}
        <div className="grid grid-cols-2 gap-4 w-full">
          <div className="flex flex-col w-full">
            <p className="label text-sm">Included in Package</p>
            <p className="text-text-primary text-sm font-medium">
              {effectiveIncludedDays} service days
            </p>
            <p className="text-text-primary text-sm font-medium">Single material pricing</p>
          </div>

          {/* Show overages if any */}
          <div className="flex flex-col">
            <p className="label text-sm">Usage & Overages</p>
            {daysInfo && (
              <>
                <p className="text-text-primary text-sm">
                  {daysInfo.actualDays} days total
                  {daysInfo.overageDays > 0 && (
                    <span className="text-warning ml-1">(+{daysInfo.overageDays} extra)</span>
                  )}
                </p>
              </>
            )}
            {/* Daily overage rate info */}
            <p className="text-text-light text-xs mt-1">
              Day overages calculated at{' '}
              {safeMoneyFromCents(dailyOverageFeeInCents || 0).toFormat()}
              /day
            </p>
          </div>
        </div>

        {/* Single Material Disclaimer */}
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded p-2 mt-2 w-full">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <AlertTriangle className="h-3 w-3 text-amber-700" aria-hidden="true" />
            </div>
            <div className="ml-2 flex-1">
              <p className="text-xs font-semibold text-amber-900">
                Requires 100% {materialName} load only
              </p>
              <p className="text-xs text-amber-800">
                Mixed loads will be billed at Pay-As-You-Go rate
              </p>
            </div>
          </div>
        </div>

        {/* Dumpster info - placed above separator */}
        <div className="mt-2">
          <p className="text-text-primary text-sm">{name} Dumpster</p>
          <p className="text-text-light text-xs">
            {quantity} dumpster{quantity > 1 ? 's' : ''}
          </p>
        </div>

        {/* Single Material Pricing Breakdown */}
        <div className="mt-3 pt-3 border-t border-primary-100">
          <div className="flex flex-row justify-between text-sm">
            <p>Base Price</p>
            <p>{safeMoneyFromCents(basePriceInCents).toFormat()}</p>
          </div>
          {/* Show day overage charge if applicable */}
          {daysInfo && daysInfo.overageDays > 0 && daysInfo.overageRate > 0 && (
            <div className="flex flex-row justify-between text-sm mt-1">
              <p className="text-text-light text-sm">
                {daysInfo.overageDays} extra day(s) ×{' '}
                {safeMoneyFromCents(daysInfo.overageRate).toFormat()}
              </p>
              <p className="text-text-light text-sm">
                {safeMoneyFromCents(daysInfo.overageDays * daysInfo.overageRate).toFormat()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
